/**
 * Replication engine.
 *
 * Drains the outbox and pulls server changes whenever a session token is available and the
 * device believes it is online. Every failure mode is treated as temporary except an
 * explicit rejection from the server, which is dropped from the queue and reported.
 */

import {
  MAX_OPS_PER_PUSH,
  RejectionReason,
  emptyChanges,
  parseAttendanceMark,
  parseMembership,
  parseNote,
  parsePerson,
  parseProject,
  parseSessionOverride,
  type Rejection,
  type SyncChanges,
  type SyncResponse,
} from '../../shared/sync-protocol.js';
import { EntityKind, type EntityKind as EntityKindType } from '../../shared/types.js';
import type { Result } from '../../shared/result.js';
import { Persistence, type OutboxEntry } from './persistence.js';
import { AppStore, SyncStatus } from './store.js';

/** Supplies the current Clerk session token, or `null` when there is no session. */
export type TokenProvider = () => Promise<string | null>;

/** Why one replication round did not complete. */
export type SyncFailure =
  | { kind: 'no_session' }
  | { kind: 'unreachable'; detail: string }
  | { kind: 'rejected_by_server'; status: number; detail: string };

/** Renders a failure as the message shown in the connection row. */
export function describeSyncFailure(failure: SyncFailure): string {
  switch (failure.kind) {
    case 'no_session': return 'Waiting for sign-in.';
    case 'unreachable': return 'No connection. Changes are saved on this device.';
    case 'rejected_by_server': return `Server refused the update (${failure.status}): ${failure.detail}`;
  }
}

/** Delay before the first retry after a failed round. */
const RETRY_BASE_MS = 2_000;

/** Ceiling on the retry delay, so a long outage still recovers promptly when it ends. */
const RETRY_MAX_MS = 60_000;

/**
 * How often a background round runs, chosen from how likely a change is to matter right now.
 *
 * Two people taking the same roll call is the case worth optimising: each needs to see the
 * other's marks quickly, and an empty round costs a few hundred bytes, which is affordable
 * even on 2G. An idle or backgrounded tab backs right off.
 */
const POLL_INTERVAL_MS = {
  /** Visible and edited or opened recently. */
  active: 8_000,
  /** Visible but untouched for a while. */
  visible: 30_000,
  /** Backgrounded; the next round happens on `visibilitychange` anyway. */
  hidden: 120_000,
} as const;

/** How long after the last interaction a tab is still treated as active. */
const ACTIVE_WINDOW_MS = 120_000;

/**
 * Delay before pulling again after this client pushed something.
 *
 * A second editor working the same session tends to push at the same time, and their records
 * are only visible from the round after theirs lands.
 */
const FOLLOW_UP_PULL_MS = 2_500;

/** Short delay used to batch a burst of local edits into one push. */
const PUSH_DEBOUNCE_MS = 400;

/** Collects the records of one entity array that parse, discarding the rest. */
function parseAll<T>(
  value: unknown,
  parse: (entry: unknown, path: string) => Result<T, unknown>,
): T[] {
  if (!Array.isArray(value)) return [];
  const kept: T[] = [];
  for (const [index, entry] of value.entries()) {
    const parsed = parse(entry, String(index));
    if (parsed.ok) kept.push(parsed.value);
  }
  return kept;
}

/** Reads the `changes` field of a response, keeping only well-formed records. */
function decodeChanges(value: unknown): SyncChanges {
  if (typeof value !== 'object' || value === null) return emptyChanges();
  const record = value as Record<string, unknown>;
  return {
    projects: parseAll(record.projects, parseProject),
    memberships: parseAll(record.memberships, parseMembership),
    people: parseAll(record.people, parsePerson),
    sessionOverrides: parseAll(record.sessionOverrides, parseSessionOverride),
    attendanceMarks: parseAll(record.attendanceMarks, parseAttendanceMark),
    notes: parseAll(record.notes, parseNote),
  };
}

/** Reads the rejection list, keeping entries this client understands. */
function decodeRejections(value: unknown): Rejection[] {
  if (!Array.isArray(value)) return [];
  const known = new Set<string>(Object.values(EntityKind));
  const reasons = new Set<string>(Object.values(RejectionReason));
  return value.flatMap((entry): Rejection[] => {
    if (typeof entry !== 'object' || entry === null) return [];
    const record = entry as Record<string, unknown>;
    if (typeof record.kind !== 'string' || !known.has(record.kind)) return [];
    if (typeof record.id !== 'string' || typeof record.reason !== 'string') return [];
    // A reason this client does not know about is reported as a plain refusal rather than
    // dropped, so a newer server never silences a rejection the outbox is about to discard.
    const reason = reasons.has(record.reason) ? (record.reason as RejectionReason) : RejectionReason.Malformed;
    return [{ kind: record.kind as EntityKindType, id: record.id, reason }];
  });
}

/**
 * Reads a response body as a sync response.
 *
 * @returns the decoded response, or `null` when the body is missing the fields replication
 *   cannot proceed without (cursor and identity).
 */
function decodeResponse(body: unknown): SyncResponse | null {
  if (typeof body !== 'object' || body === null) return null;
  const record = body as Record<string, unknown>;
  if (typeof record.cursor !== 'number' || !Number.isInteger(record.cursor)) return null;
  const identity = record.identity;
  if (typeof identity !== 'object' || identity === null) return null;
  const identityRecord = identity as Record<string, unknown>;
  if (typeof identityRecord.userId !== 'string' || typeof identityRecord.email !== 'string') return null;
  return {
    cursor: record.cursor,
    serverTime: typeof record.serverTime === 'number' ? record.serverTime : Date.now(),
    changes: decodeChanges(record.changes),
    rejections: decodeRejections(record.rejections),
    identity: { userId: identityRecord.userId, email: identityRecord.email },
  };
}

/** Describes a rejected op for the UI, naming what was refused rather than an opaque code. */
function describeRejection(rejection: Rejection): string {
  const subject: Record<EntityKind, string> = {
    [EntityKind.Project]: 'a project change',
    [EntityKind.Membership]: 'a member change',
    [EntityKind.Person]: 'a roster change',
    [EntityKind.SessionOverride]: 'a calendar change',
    [EntityKind.AttendanceMark]: 'an attendance mark',
    [EntityKind.Note]: 'a session note',
  };
  const cause: Record<RejectionReason, string> = {
    [RejectionReason.NotAMember]: 'you are no longer a member of that project',
    [RejectionReason.NotOwner]: 'only the owner may do that',
    [RejectionReason.Malformed]: 'the record was not readable',
    [RejectionReason.WrongProject]: 'it belongs to another project',
  };
  return `${subject[rejection.kind]} was refused: ${cause[rejection.reason]}`;
}

/** Runs replication rounds and keeps the store's sync state current. */
export class SyncEngine {
  private running = false;

  private queuedRound = false;

  private retryDelay = RETRY_BASE_MS;

  private timer: ReturnType<typeof setTimeout> | null = null;

  private disposed = false;

  /** When the person last did something that makes fresh data matter. */
  private lastActivityAt = Date.now();

  /** Whether the round that just finished delivered records to the server. */
  private pushedThisRound = false;

  /**
   * @param store replica to merge pulled changes into.
   * @param persistence outbox and cursor storage.
   * @param getToken supplies the session token; returning `null` pauses replication.
   */
  constructor(
    private readonly store: AppStore,
    private readonly persistence: Persistence,
    private getToken: TokenProvider,
  ) {}

  /** Replaces the token provider, for example once Clerk finishes loading. */
  setTokenProvider(getToken: TokenProvider): void {
    this.getToken = getToken;
    this.schedule(0);
  }

  /** Starts background replication and the online/offline listeners. */
  start(): void {
    this.disposed = false;
    this.store.onLocalWrite = () => { this.noteActivity(); this.schedule(PUSH_DEBOUNCE_MS); };
    this.store.onRefreshRequest = () => { this.noteActivity(); this.schedule(0); };
    globalThis.addEventListener('online', this.handleOnline);
    globalThis.addEventListener('offline', this.handleOffline);
    document.addEventListener('visibilitychange', this.handleVisibility);
    this.schedule(0);
  }

  /** Stops background replication and removes listeners. */
  stop(): void {
    this.disposed = true;
    if (this.timer !== null) clearTimeout(this.timer);
    globalThis.removeEventListener('online', this.handleOnline);
    globalThis.removeEventListener('offline', this.handleOffline);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    this.store.onLocalWrite = null;
    this.store.onRefreshRequest = null;
  }

  /** Records that the person is actively using the app, which shortens the poll interval. */
  private noteActivity(): void {
    this.lastActivityAt = Date.now();
  }

  /** Returns the background interval that matches the tab's current state. */
  private pollInterval(): number {
    if (document.visibilityState !== 'visible') return POLL_INTERVAL_MS.hidden;
    return Date.now() - this.lastActivityAt < ACTIVE_WINDOW_MS
      ? POLL_INTERVAL_MS.active
      : POLL_INTERVAL_MS.visible;
  }

  /** Reacts to the browser regaining connectivity by syncing immediately. */
  private handleOnline = (): void => { this.retryDelay = RETRY_BASE_MS; this.schedule(0); };

  /** Reacts to the browser losing connectivity by showing the offline state. */
  private handleOffline = (): void => {
    this.store.setSyncState({ status: SyncStatus.Offline, lastError: null });
  };

  /** Syncs when the tab becomes visible again, which is when stale data is most noticeable. */
  private handleVisibility = (): void => {
    if (document.visibilityState !== 'visible') return;
    this.noteActivity();
    this.schedule(0);
  };

  /** Requests a round after `delayMs`, collapsing overlapping requests into one. */
  schedule(delayMs: number): void {
    if (this.disposed) return;
    if (this.running) { this.queuedRound = true; return; }
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => { void this.runRound(); }, delayMs);
  }

  /** Performs one push-and-pull round and schedules the next one. */
  private async runRound(): Promise<void> {
    this.running = true;
    this.timer = null;
    const outcome = await this.round();
    this.running = false;

    if (outcome === null) {
      this.retryDelay = RETRY_BASE_MS;
      const pushed = this.pushedThisRound;
      this.pushedThisRound = false;
      if (this.queuedRound) this.schedule(0);
      else this.schedule(pushed ? FOLLOW_UP_PULL_MS : this.pollInterval());
    } else {
      this.retryDelay = Math.min(this.retryDelay * 2, RETRY_MAX_MS);
      this.schedule(outcome.kind === 'no_session' ? this.pollInterval() : this.retryDelay);
    }
    this.queuedRound = false;
  }

  /**
   * Pushes queued writes and merges the server's answer.
   *
   * @returns `null` on success, otherwise the failure that stopped the round.
   */
  private async round(): Promise<SyncFailure | null> {
    if (!navigator.onLine) {
      this.store.setSyncState({ status: SyncStatus.Offline, lastError: null });
      return { kind: 'unreachable', detail: 'browser reports no network' };
    }

    let token: string | null;
    try {
      token = await this.getToken();
    } catch {
      token = null;
    }
    if (token === null) {
      this.store.setSyncState({ status: SyncStatus.SignedOut, lastError: null });
      return { kind: 'no_session' };
    }

    const queued = await this.persistence.readOutbox();
    const batch = queued.slice(0, MAX_OPS_PER_PUSH);
    this.pushedThisRound = batch.length > 0;
    this.store.setSyncState({ status: SyncStatus.Syncing, pending: queued.length });

    let response: Response;
    try {
      response = await fetch('/api/sync', {
        method: 'POST',
        headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
        body: JSON.stringify({ since: this.store.cursor, ops: batch.map((entry) => entry.op) }),
      });
    } catch (cause) {
      const detail = cause instanceof Error ? cause.message : 'network error';
      this.store.setSyncState({ status: SyncStatus.Offline, lastError: null });
      return { kind: 'unreachable', detail };
    }

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      const failure: SyncFailure = { kind: 'rejected_by_server', status: response.status, detail: detail.slice(0, 200) };
      // A 401 means the cached session expired; replication resumes once Clerk refreshes it.
      this.store.setSyncState({
        status: response.status === 401 ? SyncStatus.SignedOut : SyncStatus.Failed,
        lastError: response.status === 401 ? null : describeSyncFailure(failure),
      });
      return failure;
    }

    const decoded = decodeResponse(await response.json().catch(() => null));
    if (decoded === null) {
      const failure: SyncFailure = { kind: 'rejected_by_server', status: response.status, detail: 'unreadable response' };
      this.store.setSyncState({ status: SyncStatus.Failed, lastError: describeSyncFailure(failure) });
      return failure;
    }

    await this.store.adoptIdentity(decoded.identity);
    await this.store.absorbChanges(decoded.changes, decoded.cursor);
    await this.clearDelivered(batch);

    const remaining = await this.persistence.readOutbox();
    const rejectionNote = decoded.rejections.length === 0
      ? null
      : decoded.rejections.map(describeRejection).join('; ');
    this.store.setSyncState({
      status: remaining.length > 0 ? SyncStatus.Syncing : SyncStatus.Synced,
      pending: remaining.length,
      lastSyncedAt: Date.now(),
      lastError: rejectionNote,
    });
    if (remaining.length > 0) this.queuedRound = true;
    return null;
  }

  /** Removes the delivered batch from the outbox. */
  private async clearDelivered(batch: readonly OutboxEntry[]): Promise<void> {
    const ids = batch.map((entry) => entry.id).filter((id): id is number => id !== undefined);
    await this.persistence.dequeue(ids);
  }
}
